import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { RefreshCw, Key, Plus, Trash2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { API, PLATFORMS, getPlatformName } from "./constants";

const SettingsCredentialsTab = ({ lang = "en" }) => {
  const [credentials, setCredentials] = useState([]);
  const [credDialogOpen, setCredDialogOpen] = useState(false);
  const [newCred, setNewCred] = useState({ platform: "", api_key: "", api_secret: "", access_token: "" });
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/settings/credentials`);
      setCredentials(res.data);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const saveCredential = async () => {
    try {
      await axios.post(`${API}/settings/credentials`, newCred);
      toast.success("Credential saved");
      setCredDialogOpen(false);
      setNewCred({ platform: "", api_key: "", api_secret: "", access_token: "" });
      fetchData();
    } catch {
      toast.error("Failed to save credential");
    }
  };

  const deleteCredential = async (platform) => {
    try {
      await axios.delete(`${API}/settings/credentials/${platform}`);
      toast.success("Credential deleted");
      fetchData();
    } catch {
      toast.error("Failed to delete credential");
    }
  };

  if (loading) return <div className="flex justify-center py-12"><RefreshCw className="h-6 w-6 animate-spin" /></div>;

  return (
    <div className="space-y-4">
      <Card className="bg-card border-border/50">
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle className="text-lg font-heading">
              {lang === "ko" ? "플랫폼 API 자격증명" : lang === "zh" ? "平台API凭证" : "Platform API Credentials"}
            </CardTitle>
            <CardDescription>
              {lang === "ko" ? "플랫폼 API 키와 토큰을 관리합니다" : lang === "zh" ? "管理平台API密钥和令牌" : "Manage your platform API keys and tokens"}
            </CardDescription>
          </div>
          <Dialog open={credDialogOpen} onOpenChange={setCredDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" data-testid="add-credential-btn">
                <Plus className="h-4 w-4 mr-1" />
                {lang === "ko" ? "자격증명 추가" : lang === "zh" ? "添加凭证" : "Add Credential"}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{lang === "ko" ? "API 자격증명 추가" : lang === "zh" ? "添加API凭证" : "Add API Credential"}</DialogTitle>
                <DialogDescription>{lang === "ko" ? "플랫폼 API 자격증명을 입력하세요" : lang === "zh" ? "输入平台API凭证" : "Enter your platform API credentials"}</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>{lang === "ko" ? "플랫폼" : lang === "zh" ? "平台" : "Platform"}</Label>
                  <Select value={newCred.platform} onValueChange={(v) => setNewCred({ ...newCred, platform: v })}>
                    <SelectTrigger><SelectValue placeholder={lang === "ko" ? "플랫폼 선택" : lang === "zh" ? "选择平台" : "Select platform"} /></SelectTrigger>
                    <SelectContent>{PLATFORMS.filter(p => p.id !== "all").map((p) => <SelectItem key={p.id} value={p.id}>{getPlatformName(p, lang)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{lang === "ko" ? "API 키" : lang === "zh" ? "API密钥" : "API Key"}</Label>
                  <Input type="password" value={newCred.api_key} onChange={(e) => setNewCred({ ...newCred, api_key: e.target.value })} placeholder={lang === "ko" ? "API 키 입력" : lang === "zh" ? "输入API密钥" : "Enter API key"} />
                </div>
                <div>
                  <Label>{lang === "ko" ? "API 시크릿 (선택)" : lang === "zh" ? "API密钥 (可选)" : "API Secret (optional)"}</Label>
                  <Input type="password" value={newCred.api_secret} onChange={(e) => setNewCred({ ...newCred, api_secret: e.target.value })} />
                </div>
                <div>
                  <Label>{lang === "ko" ? "액세스 토큰 (선택)" : lang === "zh" ? "访问令牌 (可选)" : "Access Token (optional)"}</Label>
                  <Input type="password" value={newCred.access_token} onChange={(e) => setNewCred({ ...newCred, access_token: e.target.value })} />
                </div>
              </div>
              <DialogFooter>
                <Button onClick={saveCredential} data-testid="save-credential-btn">
                  {lang === "ko" ? "저장" : lang === "zh" ? "保存" : "Save Credential"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          {credentials.length === 0 ? (
            <p className="text-center text-muted-foreground py-8" data-testid="no-credentials-msg">
              {lang === "ko" ? "등록된 자격증명이 없습니다" : lang === "zh" ? "暂无已配置的凭证" : "No credentials configured"}
            </p>
          ) : (
            <div className="space-y-3">
              {credentials.map((cred) => (
                <div key={cred.platform} className="flex flex-col gap-3 rounded-lg border border-border/50 bg-muted/30 p-4 sm:flex-row sm:items-center sm:justify-between" data-testid={`credential-item-${cred.platform}`}>
                  <div className="flex min-w-0 items-center gap-3">
                    <Key className="h-4 w-4 text-primary" />
                    <div className="min-w-0">
                      <p className="font-medium capitalize">{cred.platform}</p>
                      <p className="text-xs text-muted-foreground">
                        API Key: {cred.api_key_set ? "••••••••" : "Not set"}
                        {cred.api_secret_set && " | Secret: ••••••••"}
                        {cred.access_token_set && " | Token: ••••••••"}
                      </p>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => deleteCredential(cred.platform)} className="w-full sm:w-auto" data-testid={`delete-credential-${cred.platform}`}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SettingsCredentialsTab;
